import http from './api'
import type { CancelToken } from 'axios'
import { Prediction, MatchWithPredictions } from '../types/match'
import { DetailedPrediction } from '../types/detailedPrediction'
import { CreatePredictionData, UpdatePredictionData } from '../types/prediction'

/** Shape returned by GET /predictions/best/:roomId (each item in the array) */
export interface BestPredictionFromApi {
  _id: string
  id?: string
  winner: string | null
  predictedScore: string
  match: { _id?: string; id?: string; teamA: string; teamB: string; winner: string | null; score: string; date: string; time: string; league?: string; number?: string }
  player: { _id: string; id?: string; name: string; avatar?: string | null }
  predictedScorePoint?: number
  predictedWinnerPoint?: number
  predictedWinnerAdditionalTimePoint?: number | null
  predictedWinnerPenaltiesPoint?: number | null
}

export interface PlayedMatch {
  _id: string
  id: string
  teamA: string
  teamB: string
  date: string
  time: string
  score: string
  winner: string | null
  league: string
  playoff?: boolean
  firstLegMatchScore?: string | null
  winnerAdditionalTime?: string | null
  winnerPenalties?: string | null
  liveScore?: string | null
  liveStatus?: string | null
}

export interface MatchPlayerPrediction {
  _id: string
  playerId: string
  playerName: string
  winner: string | null
  predictedScore: string
  predictedWinnerPoint: number
  predictedScorePoint: number
  totalPoints: number
  predictedWinnerAdditionalTime?: string | null
  predictedWinnerPenalties?: string | null
  predictedWinnerAdditionalTimePoint?: number | null
  predictedWinnerPenaltiesPoint?: number | null
}

export interface MatchPredictionsResponse {
  match: { _id: string; teamA: string; teamB: string; score: string; winner: string | null; date: string; time: string; league?: string; playoff?: boolean; playoffUniqueGame?: boolean; firstLegMatchScore?: string | null; winnerAdditionalTime?: string | null; winnerPenalties?: string | null; liveScore?: string | null; liveStatus?: string | null } | null
  predictions: MatchPlayerPrediction[]
}

export interface AdminMatchListItem {
  id: string
  _id?: string
  index?: string
  number?: string | null
  date: string
  time: string
  teamA: string
  teamB: string
  league: string
  score: string | null
  winner: string | null
  playoff?: boolean
  playoffUniqueGame?: boolean
  firstLegMatchScore?: string | null
  winnerAdditionalTime?: string | null
  winnerPenalties?: string | null
  liveScore?: string | null
  liveStatus?: string | null
}

export interface AdminMatchListResponse {
  matches: AdminMatchListItem[]
  total: number
  upcomingTotal?: number
  pastTotal?: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminMatchUpdatePayload {
  teamA: string
  teamB: string
  league: string
  score?: string
  winner?: string | null
  playoff?: boolean
  playoffUniqueGame?: boolean
  firstLegMatchScore?: string | null
  winnerAdditionalTime?: string | null
  winnerPenalties?: string | null
}

export interface RoomPlayedMatchesOptions {
  playedOnly?: boolean
  futureOnly?: boolean
}

class MatchService {
  /** Admin: list all matches with pagination (GET /matches, requires AdminGuard). */
  async getAllMatches(params?: {
    search?: string
    page?: number
    limit?: number
    scope?: 'past' | 'upcoming'
  }): Promise<AdminMatchListResponse> {
    const response = await http.get<AdminMatchListResponse>('/matches', { params })
    return response.data
  }

  /** Admin: update match result (PATCH /matches/:id, requires AdminGuard). */
  async updateMatch(id: string, data: AdminMatchUpdatePayload): Promise<AdminMatchListItem> {
    const response = await http.patch<AdminMatchListItem>(`/matches/${id}`, data)
    return response.data
  }

  async getRoomMatches(
    roomId: string,
    startDate?: string,
    endDate?: string
  ): Promise<MatchWithPredictions[]> {
    const params: { startDate?: string; endDate?: string } = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    const response = await http.get(`/matches/room/${roomId}`, { params })
    return response.data
  }

  async getRoomPlayedMatches(
    roomId: string,
    limit: number = 18,
    offset: number = 0,
    options: RoomPlayedMatchesOptions = {},
  ): Promise<PlayedMatch[]> {
    const { playedOnly = false, futureOnly = false } = options
    const response = await http.get(`/matches/room/${roomId}/played`, {
      params: {
        limit,
        offset,
        ...(playedOnly ? { playedOnly: 'true' } : {}),
        ...(futureOnly ? { futureOnly: 'true' } : {}),
      },
    })
    return Array.isArray(response.data) ? response.data : []
  }

  async getPredictionsForMatch(roomId: string, matchId: string): Promise<MatchPredictionsResponse> {
    const response = await http.get(`/predictions/for-match/${roomId}/${matchId}`)
    return response.data
  }

  async deletePrediction(predictionId: string): Promise<void> {
    await http.delete(`/predictions/${predictionId}`)
  }

  async getUserPredictions(userId: string, roomId?: string): Promise<Prediction[]> {
    const params = roomId ? { roomId } : {}
    const response = await http.get(`/predictions/user/${userId}`, { params })
    return response.data
  }

  async getRoomPlayerPredictions(
    roomId: string,
    playerId: string,
    options?: { cancelToken?: CancelToken; limit?: number; offset?: number; futureOnly?: boolean; playedOnly?: boolean }
  ): Promise<{ data: DetailedPrediction[]; hasMore: boolean; total: number }> {
    const params: Record<string, number | boolean> = {}
    if (options?.limit != null) params.limit = options.limit
    if (options?.offset != null) params.offset = options.offset
    if (options?.playedOnly) params.playedOnly = true
    else if (options?.futureOnly) params.futureOnly = true
    const config = {
      ...(options?.cancelToken ? { cancelToken: options.cancelToken } : {}),
      params: Object.keys(params).length ? params : undefined,
    }
    const response = await http.get(`/predictions/list/${roomId}/${playerId}`, config)
    const raw = response.data
    // Backend returns { data, hasMore, total }; support legacy array response
    if (Array.isArray(raw)) {
      return { data: raw, hasMore: false, total: raw.length }
    }
    return {
      data: raw.data ?? [],
      hasMore: !!raw.hasMore,
      total: typeof raw.total === 'number' ? raw.total : (raw.data?.length ?? 0),
    }
  }

  async createPrediction(data: CreatePredictionData): Promise<Prediction> {
    const response = await http.post('/predictions', data)
    return response.data
  }

  async updatePrediction(roomId: string, matchId: string, data: UpdatePredictionData): Promise<Prediction> {
    const response = await http.patch(`/predictions/${roomId}/${matchId}`, data)
    return response.data
  }

  async getPrediction(roomId: string, matchId: string): Promise<Prediction | null> {
    try {
      const response = await http.get(`/predictions/${roomId}/${matchId}`)
      return response.data
    } catch (error) {
      return null
    }
  }

  async getAllRoomPredictions(roomId: string): Promise<Record<string, Prediction>> {
    try {
      const response = await http.get(`/predictions/all/${roomId}`)
      const raw = response.data
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, Prediction>
      return {}
    } catch {
      return {}
    }
  }

  /**
   * Get best predictions (perfect score + correct result) for a room.
   * Lightweight alternative to fetching the full room when opening the Best Of modal.
   */
  async getBestPredictions(roomId: string): Promise<BestPredictionFromApi[]> {
    const response = await http.get(`/predictions/best/${roomId}`)
    return (Array.isArray(response.data) ? response.data : []) as BestPredictionFromApi[]
  }

  async getPlayerPredictionStats(
    roomId: string,
    playerId: string,
    period: '30d' | 'all' = '30d',
  ) {
    const response = await http.get(`/predictions/stats/${roomId}/${playerId}`, {
      params: { period },
    })
    return response.data
  }

  async debugRoomMatches(roomId: string): Promise<any> {
    console.log('=== DEBUG SERVICE CALL ===');
    console.log('Calling debug endpoint for room:', roomId);
    try {
      const response = await http.get(`/matches/debug/room/${roomId}`)
      console.log('Debug response:', response.data);
      return response.data
    } catch (error) {
      console.error('Debug endpoint error:', error);
      throw error;
    }
  }
}

const matchService = new MatchService()
export default matchService
